import React from 'react';
import { Modal, ModalProps } from '../../../Components/Modal/Modal';
import { Notification } from '../../../Components/Notification';
import * as MantraModal from '../../../globalStyles/modal';
import { Text } from '../styles';
import { WarningWithIcon } from '../../../Components/Warning';
import { FinalButton } from '../../../Components/FinalButton';
import { MantraAdminOnly, useCurrentProvider } from '../../../Components/Permissions';
import { SupportLink } from '../../../Components/SupportLink';
import { useDrilldownContext } from '../helpers';
import {
  ReferralPartners,
  ReferralSource,
  useCharlieHealthConfigQuery,
  useCreatePartnerReferralMutation,
} from '../../../graphQL';
import moment from 'moment-timezone';

type IOPModalProps = Omit<ModalProps, 'size' | 'children'> & {
  onClose: () => void;
};

export const IOPModal = ({ onClose, ...props }: IOPModalProps) => {
  const { currentProvider } = useCurrentProvider();
  const { user } = useDrilldownContext();
  const {
    data: charlieHealthData,
    loading: charlieHealthLoading,
    error: charlieHealthError,
  } = useCharlieHealthConfigQuery();
  const charlieHealthMinAge = charlieHealthData?.charlieHealthConfig.minAge;
  const charlieHealthMaxAge = charlieHealthData?.charlieHealthConfig.maxAge;
  const [createPartnerReferral, { error, loading }] = useCreatePartnerReferralMutation();
  const birthdayError = !user.birthDate;
  const age = user.birthDate ? moment().diff(moment(user.birthDate), 'years') : null;
  const ineligibleBirthdayError =
    charlieHealthMinAge !== undefined &&
    charlieHealthMaxAge !== undefined &&
    age !== null &&
    (age < charlieHealthMinAge || age > charlieHealthMaxAge);

  const handleOnClick = async () => {
    if (birthdayError) {
      return;
    }
    const iopCreated = await createPartnerReferral({
      variables: {
        data: {
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          birthDate: user.birthDate!.toString(),
          preferredName: user.preferredName,
          providerId: currentProvider.id,
          referralSource: ReferralSource.Oz,
          partner: ReferralPartners.CharlieHealth,
        },
      },
    });
    if (iopCreated) {
      window.open('https://www.charliehealth.com/mantra');
      onClose();
    }
  };
  return (
    <MantraAdminOnly>
      <Modal size="small" onClose={onClose} {...props}>
        {error && <Notification kind="negative">{error.message}</Notification>}
        {charlieHealthError && (
          <Notification kind="negative">
            Unable to complete referral, please reach out to <SupportLink>support</SupportLink> to
            resolve.
          </Notification>
        )}

        {!charlieHealthError && birthdayError && (
          <Notification kind="negative">
            User does not have a birthday. Please enter a birthday for the user profile.
            {charlieHealthMinAge !== undefined && ` User must be over ${charlieHealthMinAge}.`}
          </Notification>
        )}
        {ineligibleBirthdayError && (
          <Notification kind="negative">
            {`User has an ineligible birthday. User must be between ${charlieHealthMinAge} and ${charlieHealthMaxAge} to be eligible.`}
          </Notification>
        )}
        <Text.h2 className="mb4">
          Are you sure you want to refer this patient to IOP (Charlie Health)?
        </Text.h2>

        <div className="mb4">
          <div className="flex flex-column gap-2">
            <Text.body>
              Clicking the button below will
              <strong> add a referral event to this patient account timeline </strong>
              before the referral is completed.
            </Text.body>
            <WarningWithIcon>
              <Text.bodySmallBold>
                <u>Note</u>: You will be sent to Charlie Health’s referral form, which must be
                submitted to in order to successfully complete the IOP referral.
              </Text.bodySmallBold>
            </WarningWithIcon>
            <FinalButton
              onClick={handleOnClick}
              kind="primary"
              loading={loading}
              disabled={
                charlieHealthLoading ||
                !!charlieHealthError ||
                birthdayError ||
                ineligibleBirthdayError
              }
            >
              Yes, Continue to IOP Referral Form
            </FinalButton>
          </div>
        </div>
        <MantraModal.closeLink className="b" onClick={onClose}>
          Cancel
        </MantraModal.closeLink>
      </Modal>
    </MantraAdminOnly>
  );
};
